import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import apiClient from '../api/client';
import './ConfirmarRodada.css';

/**
 * Página para jogadores confirmarem presença em uma rodada
 */

const ConfirmarRodada = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin, role } = useAuth();
  const [rodada, setRodada] = useState(null);
  const [confirmado, setConfirmado] = useState(false);
  const [loading, setLoading] = useState(true);
  const [processando, setProcessando] = useState(false);
  const [error, setError] = useState('');
  const [confirmacoes, setConfirmacoes] = useState([]);
  const [jogadores, setJogadores] = useState([]);

  useEffect(() => {
    carregarDados();
  }, [id]);

  const carregarDados = async () => {
    try {
      setLoading(true);
      const [rodadaRes, confirmacoesRes, jogadoresRes] = await Promise.all([
        apiClient.get(`/rodadas`).then(res => {
          const rodadaEncontrada = res.data.find(r => r.id === id);
          if (!rodadaEncontrada) {
            throw new Error('Rodada não encontrada');
          }
          return rodadaEncontrada;
        }),
        apiClient.get(`/rodadas/${id}/confirmacoes`).catch((err) => {
          console.error('Erro ao buscar confirmações:', err);
          return { data: [] };
        }),
        // Buscar jogadores apenas se for admin
        isAdmin ? apiClient.get('/jogadores').catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ]);

      setRodada(rodadaRes);
      
      // Armazenar confirmações e jogadores
      if (confirmacoesRes && confirmacoesRes.data) {
        setConfirmacoes(confirmacoesRes.data);
        // Verificar se o usuário já confirmou
        if (user && user.id) {
          const jaConfirmou = confirmacoesRes.data.some(c => c.jogador_id === user.id || c.jogador?.id === user.id);
          setConfirmado(jaConfirmou);
        } else {
          setConfirmado(false);
        }
      } else {
        setConfirmacoes([]);
        setConfirmado(false);
      }
      
      if (jogadoresRes && jogadoresRes.data) {
        setJogadores(jogadoresRes.data);
      }
      
      setError('');
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError(err.response?.data?.error || err.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmar = async () => {
    if (!window.confirm('Confirmar presença nesta rodada?\n\nSerá cobrado:\n- Entrada: R$ 40,00\n- Buy-in: R$ 25,00\n\nTotal: R$ 65,00')) {
      return;
    }

    try {
      setProcessando(true);
      setError('');
      
      console.log('Confirmando presença na rodada:', id);
      const response = await apiClient.post(`/rodadas/${id}/confirmar-presenca`);
      console.log('Resposta da confirmação:', response.data);
      
      alert('Presença confirmada com sucesso!\n\nValor cobrado: R$ 65,00 (R$ 40,00 entrada + R$ 25,00 buy-in)');
      setConfirmado(true);
      await carregarDados();
      
      // Redirecionar para home após confirmação
      if (isAdmin || role === 'gerenciador') {
        navigate('/dashboard');
      } else {
        navigate('/dashboard-jogador');
      }
    } catch (err) {
      console.error('Erro ao confirmar presença:', err);
      console.error('Detalhes do erro:', err.response?.data);
      setError(err.response?.data?.error || err.message || 'Erro ao confirmar presença');
    } finally {
      setProcessando(false);
    }
  };

  const handleCancelar = async () => {
    if (!window.confirm('Deseja cancelar sua confirmação de presença?\n\nO valor já cobrado não será reembolsado.')) {
      return;
    }

    try {
      setProcessando(true);
      setError('');
      
      await apiClient.post(`/rodadas/${id}/confirmar-presenca`);
      
      alert('Confirmação cancelada');
      setConfirmado(false);
      carregarDados();
    } catch (err) {
      console.error('Erro ao cancelar confirmação:', err);
      setError(err.response?.data?.error || 'Erro ao cancelar confirmação');
    } finally {
      setProcessando(false);
    }
  };

  const handleRemoverJogador = async (jogadorId) => {
    const jogador = jogadores.find(j => j.id === jogadorId);
    const nomeJogador = jogador?.nome || 'este jogador';
    
    if (!window.confirm(`Deseja remover ${nomeJogador} desta rodada?\n\nEsta ação irá cancelar a confirmação de presença do jogador.`)) {
      return;
    }
    
    try {
      setProcessando(true);
      setError('');
      
      await apiClient.delete(`/rodadas/${id}/jogador/${jogadorId}`);
      
      alert(`${nomeJogador} removido da rodada com sucesso!`);
      await carregarDados();
    } catch (err) {
      console.error('Erro ao remover jogador:', err);
      setError(err.response?.data?.error || 'Erro ao remover jogador');
      alert(err.response?.data?.error || 'Erro ao remover jogador');
    } finally {
      setProcessando(false);
    }
  };

  if (loading) {
    return <div className="container">Carregando...</div>;
  }

  if (!rodada) {
    return (
      <div className="container">
        <div className="error">Rodada não encontrada</div>
        <button 
          className="btn" 
          onClick={() => {
            if (isAdmin || role === 'gerenciador') {
              navigate('/dashboard');
            } else {
              navigate('/dashboard-jogador');
            }
          }}
        >
          ← Voltar para Home
        </button>
      </div>
    );
  }

  if (rodada.status === 'fechada') {
    return (
      <div className="container">
        <div className="error">Esta rodada já está fechada</div>
        <button 
          className="btn" 
          onClick={() => {
            if (isAdmin || role === 'gerenciador') {
              navigate('/dashboard');
            } else {
              navigate('/dashboard-jogador');
            }
          }}
        >
          ← Voltar para Home
        </button>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="page-header">
        <h1>🎲 Confirmar Presença</h1>
        <button 
          className="btn" 
          onClick={() => {
            // Ir para home ao invés de voltar para rodadas
            if (isAdmin || role === 'gerenciador') {
              navigate('/dashboard');
            } else {
              navigate('/dashboard-jogador');
            }
          }}
        >
          ← Voltar para Home
        </button>
      </div>

      {error && <div className="error">{error}</div>}

      <div className="confirmar-rodada-card">
        <div className="rodada-info">
          <h2>Rodada de {new Date(rodada.data).toLocaleDateString('pt-BR', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}</h2>
          
          <div className="info-detalhes">
            <p><strong>Custo da Janta:</strong> {rodada.custo_janta ? `R$ ${parseFloat(rodada.custo_janta).toFixed(2)}` : 'Não informado'}</p>
            <p><strong>Jackpot:</strong> {rodada.jackpot_ativado ? 'Ativado' : 'Não ativado'}</p>
          </div>
        </div>

        <div className="confirmacao-info">
          <h3>Valores a serem cobrados:</h3>
          <ul>
            <li>Entrada: <strong>R$ 40,00</strong></li>
            <li>Buy-in Simples: <strong>R$ 25,00</strong></li>
            <li className="total">Total: <strong>R$ 65,00</strong></li>
          </ul>
          
          <div className="observacoes">
            <p><small>💡 <strong>Observações:</strong></small></p>
            <ul>
              <li>O valor será cobrado imediatamente ao confirmar</li>
              <li>Você pode cancelar sua confirmação, mas o valor não será reembolsado</li>
              <li>Buy-ins adicionais e add-ons serão registrados no fechamento da rodada</li>
            </ul>
          </div>
        </div>

        {/* Seção para Admin ver e remover jogadores confirmados */}
        {isAdmin && rodada.status === 'aberta' && confirmacoes.length > 0 && (
          <div className="admin-confirmacoes-section" style={{ 
            marginTop: '30px', 
            padding: '20px', 
            background: '#f5f5f5', 
            borderRadius: '8px' 
          }}>
            <h3>👥 Participantes Confirmados ({confirmacoes.length})</h3>
            <div style={{ marginTop: '15px' }}>
              {confirmacoes.map((confirmacao) => {
                const jogadorId = confirmacao.jogador_id || confirmacao.jogador?.id;
                const jogador = jogadores.find(j => j.id === jogadorId) || confirmacao.jogador;
                const nomeJogador = jogador?.nome || 'Desconhecido';
                
                return (
                  <div key={jogadorId || confirmacao.id} style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    padding: '10px',
                    marginBottom: '10px',
                    background: 'white',
                    borderRadius: '4px',
                    border: '1px solid #ddd'
                  }}>
                    <span>{nomeJogador}</span>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleRemoverJogador(jogadorId)}
                      disabled={processando}
                      style={{ padding: '5px 10px', fontSize: '0.85em' }}
                    >
                      🗑️ Remover
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="confirmacao-actions">
          {confirmado ? (
            <>
              <div className="status-confirmado">
                <span className="badge badge-success">✓ Presença Confirmada</span>
                <p>Você já confirmou sua presença nesta rodada.</p>
              </div>
              <button 
                className="btn btn-danger" 
                onClick={handleCancelar}
                disabled={processando}
              >
                {processando ? 'Cancelando...' : 'Cancelar Confirmação'}
              </button>
            </>
          ) : (
            <button 
              className="btn btn-primary btn-large" 
              onClick={handleConfirmar}
              disabled={processando}
            >
              {processando ? 'Confirmando...' : 'Confirmar Presença (R$ 65,00)'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConfirmarRodada;
